import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, User, MapPin, Phone, Globe, Search } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import AdSlot from "../components/AdSlot";

export default function DirectoryPage() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const { data: listings = [] } = useQuery({ queryKey: ["listings"], queryFn: async () => (await api.get("/listings")).data });

  const filtered = listings.filter((l) =>
    (type === "all" || l.type === type) &&
    (l.name.toLowerCase().includes(q.toLowerCase()) || (l.address || "").toLowerCase().includes(q.toLowerCase()) || (l.category || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8">
        <span className="label-tech text-primary">Members</span>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-black mt-2">Industry Directory</h1>
        <p className="text-muted-foreground mt-2">Contractors, engineers, and suppliers across the network.</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-8">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, city, or category…" className="rounded-none pl-10 h-12" data-testid="directory-search" />
        </div>
        {["all", "company", "professional"].map((t) => (
          <button key={t} onClick={() => setType(t)} className={`px-4 py-2 text-sm font-bold border capitalize ${type === t ? "bg-primary text-white border-primary" : "border-foreground hover:bg-foreground hover:text-white"}`} data-testid={`dir-filter-${t}`}>
            {t === "all" ? "Everyone" : t === "company" ? "Companies" : t + "s"}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9 grid sm:grid-cols-2 gap-0 border border-border">
          {filtered.map((l, i) => (
            <div key={l.id} className="p-6 border-border border-b sm:[&:nth-child(odd)]:border-r animate-slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`directory-card-${l.id}`}>
              <div className="flex items-center gap-2 mb-2">
                {l.type === "professional" ? <User className="w-5 h-5 text-blue-500" /> : <Building2 className="w-5 h-5 text-primary" />}
                <span className="label-tech text-muted-foreground">{l.category}</span>
              </div>
              <h3 className="font-display text-xl font-bold">{l.name}</h3>
              {l.description && <p className="text-sm text-muted-foreground mt-1">{l.description}</p>}
              <div className="mt-4 space-y-1 text-sm">
                {l.address && <p className="flex items-center gap-2 text-muted-foreground"><MapPin className="w-3 h-3" /> {l.address}</p>}
                {l.phone && <p className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3 h-3" /> {l.phone}</p>}
                {l.website && <a href={l.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary font-semibold"><Globe className="w-3 h-3" /> Website</a>}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="p-8 text-muted-foreground text-sm col-span-2">No members found.</p>}
        </div>
        <div className="lg:col-span-3">
          <AdSlot placement="sidebar" />
        </div>
      </div>
    </div>
  );
}
